<template>
  <div class="bank-account-container">
    <transition name="view" mode="out-in">
      <c-loader v-if="loading" :size="isMobile ? 79 : 99" />

      <form-group
        v-else
        ref="formGroup"
        :label-left="!isMobile"
        :schemas="schemas"
        :data="data"
        :disabled="submitting"
        :required-fields="requiredFields"
        @add:bankAccount="onAddBankAccount"
        @remove:bankAccount="onRemoveBankAccount"
        @file:add="onFileModalOpen"
        @file:remove="onRemoveFile"
      />
    </transition>

    <c-form-builder-modal
      v-if="showFileModal"
      label-left
      submit-button-text="Salvar"
      name="bank"
      title="Anexar Arquivos"
      v-model="filesFormData"
      :required-fields="requiredFields"
      :is-loading="submitting"
      :is-disabled="submitting"
      :schema="filesSchema"
      @close="onFileModalClose"
      @submit="onFileModalSubmit"
    />

    <dialog-modal
      v-if="removing"
      theme="error"
      title="Deseja excluir esses Dados Bancários?"
      message="As informações inseridas serão excluídas do formulário.
      Tem certeza que deseja excluir este registro?"
      :loading="submitting"
      @cancel="onRemoveBankAccountCancel"
      @confirm="onRemoveBankAccountSubmit"
    />

    <dialog-modal
      v-if="showRemoveFileModal"
      theme="error"
      title="Deseja prosseguir?"
      message="Ao prosseguir os arquivos apagados serão permanentemente excluídos,
        esta ação não pode ser desfeita."
      confirm="Excluir"
      cancel="Não, mudei de ideia"
      @cancel="showRemoveFileModal = false"
      @confirm="onFileModalSubmit(filesFormData)"
    />

    <dialog-modal
      v-if="isRemovingFile"
      theme="error"
      title="Deseja excluir o arquivo selecionado?"
      message="O arquivo será permanentemente excluído, esta ação não pode ser desfeita."
      confirm="Excluir"
      cancel="Não, mudei de ideia"
      :loading="submitting"
      @cancel="isRemovingFile = false"
      @confirm="onRemoveFileSubmit"
    />
  </div>
</template>

<script>
import { computed, getCurrentInstance } from 'vue'
import { mapState, mapMutations, mapActions } from 'vuex'
import { MediaQuery } from '@convenia/mixins'
import * as types from '@types'

import FormGroup from '@components/FormGroup'
import DialogModal from '@components/DialogModal'
import bankAccountsSchema from '@convenia/employee-organisms/SystemFields/Personal/content/forms/bankAccounts'
import useSystemFields from '@convenia/employee-organisms/SystemFields/composables/useSystemFields'

export default {
  name: 'BankAccount',

  compatConfig: { MODE: 3 },

  mixins: [ MediaQuery ],

  components: {
    FormGroup,
    DialogModal
  },

  emits: [ 'next', 'previous' ],

  setup () {
    const { proxy } = getCurrentInstance()

    const {
      formSchema,
      getFormValues,
      requiredFields,
    } = useSystemFields({
      schema: computed(() => bankAccountsSchema()),
      systemFields: computed(() => proxy.systemFields?.bankAccounts || {}),
      customFields: computed(() => proxy.customFields?.bankAccounts || {}),
    })

    return {
      formSchema,
      getFormValues,
      requiredFields,
    }
  },

  computed: {
    ...mapState({
      bankAccount: state => state.bankAccount.bankAccount,
      systemFields: state => state.bankAccount.systemFields,
      customFields: state => state.bankAccount.customFields,
      employeeId: state => state.admission.employeeUuid,
    }),

    data () {
      return {
        bankAccount: this.bankAccount.map(({ customFieldValues, ...bankAccount }) => (
          this.getFormValues(bankAccount, customFieldValues)
        ))
      }
    },

    hasAttachmentField () {
      return !!this.formSchema?.documentFiles
    },

    schemas () {
      const { documentFiles, ...schema } = this.formSchema || {}

      return {
        bankAccount: {
          title: 'Dados Bancários',
          singleTitle: 'Dado Bancário',
          singleIcon: 'piggy-bank-coin',
          addLabel: 'Adicionar novo dado bancário',
          multiple: true,
          files: this.hasAttachmentField,
          filesText: 'Deve ser anexado <b>o comprovante de conta bancária</b>.',
          fields: {
            ...schema,
            digit: { ...schema.digit, label: 'Dígito' },
            pix: { ...schema.pix, label: 'Chave Pix' },
            documentFiles: { ...documentFiles, hide: true },
          }
        }
      }
    },

    filesSchema () {
      const { documentFiles } = this.formSchema || {}

      return { documentFiles }
    },

    selected () {
      if (!this.active) return {}

      return this.bankAccount.find(({ id }) => id === this.active)
    },

    submitting: {
      get () { return this.$store.state.admission.submitting },
      set (value) { this.$store.commit(types.ADMISSION_SUBMITTING, value) }
    },

  },

  data: () => ({
    loading: true,
    removing: false,
    active: null,
    showFileModal: false,
    showRemoveFileModal: false,
    isRemovingFile: false,
    removingFile: null,
    filesFormData: {},
  }),

  mounted () {
    this.init()
  },

  methods: {
    ...mapMutations({
      addBankAccount: types.EMPLOYEE_ADD_BANK,
      saveBankAccount: types.EMPLOYEE_BANK,
      setBankAccountFiles: types.EMPLOYEE_SET_BANK_FILES,
    }),

    ...mapActions({
      getCustomFields: types.EMPLOYEE_BANK_CUSTOM_FIELDS,
      getBankAccount: types.EMPLOYEE_BANK,
      setBankAccount: types.EMPLOYEE_SET_BANK,
      setBankAccounts: types.EMPLOYEE_SET_BANKS,
      deleteBankAccount: types.EMPLOYEE_DELETE_BANK,
      uploadFiles: types.EMPLOYEE_BANK_UPLOAD_FILES,
      deleteFile: types.ADMISSION_DELETE_FILE,
    }),

    next () {
      return this.onSubmit()
    },

    previous () {
      return this.onSave()
    },

    async init () {
      await this.getBankAccount()

      this.loading = false
    },

    onAddBankAccount () {
      this.addBankAccount()
      this.$refs.formGroup.formsData.bankAccount.push(
        this.data.bankAccount[this.data.bankAccount.length - 1]
      )
    },

    onRemoveBankAccount (id) {
      this.active = id

      const data = this.$refs.formGroup.formsData
      const bankAccount = data.bankAccount.find(({ id }) => id === this.active)
      const hasContent = Object
        .entries(bankAccount)
        .filter(([ key ]) => key !== 'id')
        .some(([ , value ]) => !!value)

      if (!hasContent)
        return this.onRemoveBankAccountSubmit()

      this.removing = true
    },

    onRemoveBankAccountCancel () {
      this.active = null
      this.removing = false
    },

    async onRemoveBankAccountSubmit () {
      this.submitting = true
      await this.deleteBankAccount(this.selected)
      this.submitting = false

      this.$refs.formGroup.removeFormDataItem('bankAccount', this.active)

      this.active = null
      this.removing = false
    },

    async validate () {
      if (!this.$refs.formGroup)
        return false

      if (!await this.$refs.formGroup.validate())
        return false

      return true
    },

    async onSubmit () {
      if (!await this.validate()) return

      const data = this.$refs.formGroup?.formsData || {}

      const isNotEmpty = data => Object.values(data)
        .some(value => Array.isArray(value) ? value.length : !!value)

      const emptyBankAccounts = (data.bankAccount || [])
        .filter(({ id, documentFiles, ...fields }) => typeof id === 'string'
          && !isNotEmpty(fields)
          && !documentFiles?.length)

      const bankAccounts = (data.bankAccount || [])
        .filter(({ id, documentFiles, ...fields }) => isNotEmpty(fields) || !!documentFiles?.length)

      this.submitting = true

      Promise.allSettled(emptyBankAccounts.map(this.deleteBankAccount))

      const [ err ] = await this.setBankAccounts(bankAccounts)

      this.submitting = false

      if (err?.length)
        return this.$feedback.add({
          type: 'error',
          message: 'Erro',
          highlighted: 'ao tentar salvar Dados Bancários. Tente novamente.'
        })

      this.$feedback.add({
        type: 'success',
        message: 'Dados Bancários',
        highlighted: 'atualizados com sucesso'
      })

      this.$emit('next')
    },

    onSave () {
      if (!this.$refs.formGroup) return
      const data = this.$refs.formGroup.formsData

      this.saveBankAccount(data.bankAccount)

      this.$emit('previous')
    },

    async deleteFiles (files) {
      const { employeeId } = this

      await Promise.all(files.map(
        file => this.deleteFile({ file, employeeId }))
      )
    },

    onFileModalOpen (index) {
      const data = this.$refs.formGroup?.formsData.bankAccount[index]

      if (!data) return

      this.active = index

      this.filesFormData.documentFiles = [ ...data.documentFiles || [] ]
      this.showFileModal = true
    },

    onFileModalClose () {
      this.filesFormData = {}
      this.showFileModal = false
      this.active = null
    },

    onRemoveFile ({ file, index }) {
      this.isRemovingFile = true
      this.active = index
      this.removingFile = file
    },

    async onRemoveFileSubmit () {
      const { employeeId } = this

      this.submitting = true

      const [ err ] = await this.deleteFile({
        employeeId,
        file: this.removingFile
      })

      this.submitting = false

      if (err)
        return this.$feedback.add({
          type: 'error',
          message: 'Arquivo',
          highlighted: 'não foi excluído, tente novamente'
        })

      this.$feedback.add({
        type: 'success',
        message: 'Arquivo',
        highlighted: 'excluído com sucesso'
      })

      const data = this.$refs.formGroup?.formsData.bankAccount[this.active] || {}
      const fileIndex = data.documentFiles?.findIndex(file => file.id === this.removingFile.id)

      if (fileIndex >= 0)
        data.documentFiles.splice(fileIndex, 1)

      this.isRemovingFile = false
    },

    setFiles (id, files) {
      const data = this.$refs.formGroup?.formsData.bankAccount[this.active]

      if (data) {
        data.id = id
        data.documentFiles = files
      }

      this.setBankAccountFiles({ id, files })
    },

    async onFileModalSubmit ({ documentFiles: files }) {
      const data = this.$refs.formGroup?.formsData.bankAccount[this.active] || {}
      const { id } = this.bankAccount[this.active] || {}
      const { documentFiles: initialFiles = [] } = data

      const addedFiles = files
        .filter(file => !file.done)
        .filter(file => !initialFiles.includes(file))

      const removedFiles = initialFiles
        .filter(file => !files.includes(file))

      if (removedFiles.length > 0 && !this.showRemoveFileModal) {
        this.filesFormData = { documentFiles: files }
        this.showRemoveFileModal = true

        return
      }

      this.showRemoveFileModal = false
      this.submitting = true

      const [ err, bankAccount ] = await this.setBankAccount({ id, ...data })

      if (err) {
        this.submitting = false
        return
      }

      await this.deleteFiles(removedFiles)

      const [ uploadErr, uploadedFiles ] = await this.uploadFiles({
        id: bankAccount.id,
        files: addedFiles,
      })

      if (uploadErr) {
        this.submitting = false
        return
      }

      const updatedFiles = [ ...initialFiles, ...uploadedFiles ]
        .filter(file => !removedFiles.includes(file))
        .map(file => ({ ...file, progress: 0 }))

      this.setFiles(bankAccount?.id, updatedFiles)

      this.filesFormData = {}
      this.showFileModal = false
      this.submitting = false
      this.active = null
    },
  }
}
</script>

<style lang="scss">
.bank-account-container {
  position: relative;

  & > .c-form-group > .form > .c-form > .c-shadowed > .wrapper > .fields {
    display: flex;
    flex-flow: row wrap;

    & > * {
      flex: 1 100%;
    }

    & > [name="account"] {
      flex: .7 60%;
    }
    & > [name="accountDigit"] {
      flex: .3 30%;
      margin-left: 10px;
      min-width: 50px;
      & > .label { display: none }
    }
  }

  @include responsive (tablet, desktop) {
    margin-top: 10px;
    background: white;
    border-radius: 5px;
    box-shadow: 2px 8px 16px 0px rgba(0, 0, 0, 0.06);
  }

  @include responsive (xs-mobile, mobile) {
    & > .c-form-group {
      & > .form:first-child {
        padding-top: 30px;
        & > .c-title { display: none; }
      }
    }
  }
}
</style>
